/**
 * STYLING: Plain Tailwind ONLY!
 * Central single-image picker for vision-capable tools (Welle 3).
 * Uses the /api/v2/assets/upload pipeline. The parent decides whether to
 * render this (via supports_vision) — no internal capability flag.
 *
 * imageKey (from the parent) is the source of truth: when it is set we show
 * the "selected" state even if the local preview was lost on remount.
 */

import { useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, X } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { EID } from '@/lib/element-ids.registry';

interface Props {
  imageKey: string | null;
  onChange: (imageKey: string | null) => void;
  surchargeHint?: string;
}

interface UploadResp {
  id: string;
  preview_url: string;
}

function PickerLabel({ surchargeHint }: { surchargeHint?: string }) {
  const { t } = useTranslation();
  return (
    <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
      {t('textTools.image.label')}
      {surchargeHint && (
        <span className="ml-2 text-xs text-green-700 dark:text-green-400 font-medium">{surchargeHint}</span>
      )}
    </span>
  );
}

function SelectedImage({ previewUrl, onClear }: { previewUrl: string | null; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="relative inline-block">
      {previewUrl ? (
        <img src={previewUrl} alt={t('textTools.image.label')} className="max-w-xs max-h-48 rounded-lg border border-neutral-200 dark:border-neutral-700" />
      ) : (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-neutral-200 dark:border-neutral-700 text-sm text-neutral-600 dark:text-neutral-400">
          <ImagePlus className="h-4 w-4" aria-hidden="true" />
          {t('textTools.image.selected')}
        </div>
      )}
      <button
        type="button"
        id={EID['vision-image-clear-btn']}
        onClick={onClear}
        className="absolute top-1 right-1 p-1 rounded-full bg-neutral-900/70 hover:bg-neutral-900 text-white"
        aria-label={t('textTools.image.remove')}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

interface UploadBoxProps {
  inputRef: RefObject<HTMLInputElement | null>;
  uploading: boolean;
  error: string | null;
  onFile: (file: File) => void;
}

function UploadBox({ inputRef, uploading, error, onFile }: UploadBoxProps) {
  const { t } = useTranslation();
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
        className="hidden"
        id={EID['vision-image-file-input']}
      />
      <label
        htmlFor={EID['vision-image-file-input']}
        className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900 text-sm text-neutral-700 dark:text-neutral-300"
      >
        <ImagePlus className="h-4 w-4" aria-hidden="true" />
        {uploading ? t('common.uploading') : t('textTools.image.add')}
      </label>
      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{t('textTools.image.hint')}</p>
      {error && <p className="mt-2 text-xs text-red-600" role="alert">{error}</p>}
    </>
  );
}

export function VisionImagePicker({ imageKey, onChange, surchargeHint }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Content-Type MUST stay undefined: the browser sets it WITH the
      // multipart boundary. Forcing 'multipart/form-data' drops the boundary
      // and the server cannot parse request.files (→ 400, image ignored).
      const resp = await apiClient.post<UploadResp>('/api/v2/assets/upload', fd, {
        headers: { 'Content-Type': undefined },
      });
      onChange(resp.data.id);
      setPreviewUrl(URL.createObjectURL(file));
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="vision-image-picker">
      <PickerLabel surchargeHint={surchargeHint} />
      {imageKey ? (
        <SelectedImage previewUrl={previewUrl} onClear={handleClear} />
      ) : (
        <UploadBox inputRef={inputRef} uploading={uploading} error={error} onFile={handleFile} />
      )}
    </div>
  );
}
